import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, MapPin, Phone, EnvelopeSimple, Pencil, Trash, Camera, X } from "@phosphor-icons/react";
import { api, formatApiError } from "../lib/api";
import { devWarn } from "../lib/log";
import AuthImg from "../components/AuthImg";
import { toast } from "sonner";

const EMPTY_CUSTOMER = {
  name: "", email: "", phone: "", address: "", city: "", postcode: "", notes: "", photos: [],
  source: "", lead_status: "new", lead_value: "", lost_reason: "", tags: [],
};

const SOURCE_OPTIONS = [
  { id: "", label: "—" },
  { id: "website", label: "Website" },
  { id: "referral", label: "Referral" },
  { id: "google", label: "Google" },
  { id: "facebook", label: "Facebook" },
  { id: "trustatrader", label: "TrustATrader / MyBuilder" },
  { id: "yell", label: "Yell" },
  { id: "repeat", label: "Repeat customer" },
  { id: "walk-in", label: "Walk-in / van sign" },
  { id: "other", label: "Other" },
];

const LEAD_STATUS_OPTIONS = [
  { id: "new", label: "New" },
  { id: "contacted", label: "Contacted" },
  { id: "qualified", label: "Qualified" },
  { id: "quoted", label: "Quoted" },
  { id: "won", label: "Won" },
  { id: "lost", label: "Lost" },
];

export const LEAD_STATUS_BADGE = {
  new: "bg-slate-100 text-slate-700 border-slate-300",
  contacted: "bg-blue-50 text-blue-700 border-blue-200",
  qualified: "bg-indigo-50 text-indigo-700 border-indigo-200",
  quoted: "bg-amber-50 text-amber-800 border-amber-200",
  won: "bg-emerald-50 text-emerald-700 border-emerald-200",
  lost: "bg-rose-50 text-rose-700 border-rose-200",
};

function CustomerDialog({ open, onClose, editing, onSaved }) {
  const [form, setForm] = useState(EMPTY_CUSTOMER);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (open) setForm(editing ? { ...EMPTY_CUSTOMER, ...editing, photos: editing.photos || [] } : EMPTY_CUSTOMER);
  }, [open, editing]);

  if (!open) return null;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(data.file_id);
      }
      setForm((s) => ({ ...s, photos: [...(s.photos || []), ...uploaded] }));
      toast.success(`${uploaded.length} photo${uploaded.length > 1 ? "s" : ""} uploaded`);
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail) || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removePhoto = (fid) => setForm((s) => ({ ...s, photos: (s.photos || []).filter((p) => p !== fid) }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing?.id) {
        const { data } = await api.put(`/customers/${editing.id}`, form);
        toast.success("Customer updated");
        onSaved(data);
      } else {
        const { data } = await api.post("/customers", form);
        toast.success("Customer created");
        onSaved(data);
      }
      onClose();
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail) || "Failed to save");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 grid place-items-center p-4 overflow-y-auto" onClick={onClose}>
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        className="card-elev w-full max-w-lg p-6 my-8"
        data-testid="customer-dialog"
      >
        <div className="overline">{editing ? "Edit" : "New"} customer</div>
        <h3 className="font-display text-2xl font-bold mt-1">{editing ? editing.name : "Add a customer"}</h3>
        <div className="grid grid-cols-2 gap-3 mt-5">
          <label className="block col-span-2">
            <span className="overline">Name</span>
            <input data-testid="customer-name-input" required className="rqp mt-1.5" value={form.name} onChange={set("name")} />
          </label>
          <label className="block">
            <span className="overline">Email</span>
            <input data-testid="customer-email-input" type="email" className="rqp mt-1.5" value={form.email} onChange={set("email")} />
          </label>
          <label className="block">
            <span className="overline">Phone</span>
            <input data-testid="customer-phone-input" className="rqp mt-1.5" value={form.phone} onChange={set("phone")} />
          </label>
          <label className="block col-span-2">
            <span className="overline">Address</span>
            <input data-testid="customer-address-input" className="rqp mt-1.5" value={form.address} onChange={set("address")} />
          </label>
          <label className="block">
            <span className="overline">City</span>
            <input data-testid="customer-city-input" className="rqp mt-1.5" value={form.city} onChange={set("city")} />
          </label>
          <label className="block">
            <span className="overline">Postcode</span>
            <input data-testid="customer-postcode-input" className="rqp mt-1.5" value={form.postcode} onChange={set("postcode")} />
          </label>
          <label className="block col-span-2">
            <span className="overline">Notes</span>
            <textarea data-testid="customer-notes-input" rows={3} className="rqp mt-1.5" value={form.notes} onChange={set("notes")} />
          </label>

          {/* CRM fields */}
          <div className="col-span-2 border-t border-slate-200 pt-4 mt-1">
            <div className="overline mb-2">Lead pipeline</div>
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <span className="overline">Source</span>
                <select className="rqp mt-1.5" value={form.source || ""} onChange={set("source")} data-testid="customer-source-select">
                  {SOURCE_OPTIONS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="overline">Lead status</span>
                <select className="rqp mt-1.5" value={form.lead_status || "new"} onChange={set("lead_status")} data-testid="customer-status-select">
                  {LEAD_STATUS_OPTIONS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                </select>
              </label>
              <label className="block">
                <span className="overline">Forecast value (£)</span>
                <input
                  type="number"
                  step="50"
                  min="0"
                  className="rqp mt-1.5"
                  value={form.lead_value ?? ""}
                  onChange={(e) => setForm({ ...form, lead_value: e.target.value === "" ? "" : Number(e.target.value) })}
                  data-testid="customer-value-input"
                />
              </label>
              {form.lead_status === "lost" && (
                <label className="block">
                  <span className="overline">Lost reason</span>
                  <input className="rqp mt-1.5" value={form.lost_reason || ""} onChange={set("lost_reason")} data-testid="customer-lost-reason" />
                </label>
              )}
            </div>
          </div>

          <div className="col-span-2">
            <div className="flex items-center justify-between mb-2">
              <span className="overline">Site photos</span>
              <label className="btn-secondary text-xs flex items-center gap-1.5 cursor-pointer" data-testid="customer-photos-upload">
                <Camera size={14} weight="bold" /> {uploading ? "Uploading…" : "Add photos"}
                <input type="file" multiple accept="image/*" className="hidden" onChange={onFiles} disabled={uploading} />
              </label>
            </div>
            {(form.photos || []).length === 0 ? (
              <div className="border border-dashed border-slate-300 rounded-sm p-4 text-center text-xs text-slate-500">
                No photos yet. JPG/PNG/WEBP up to 10MB each.
              </div>
            ) : (
              <div className="grid grid-cols-4 gap-2">
                {form.photos.map((fid) => (
                  <div key={fid} className="relative aspect-square border border-slate-300 overflow-hidden bg-slate-50" data-testid={`customer-photo-${fid}`}>
                    <AuthImg fileId={fid} className="w-full h-full object-cover" />
                    <button type="button" onClick={() => removePhoto(fid)} className="absolute top-1 right-1 w-6 h-6 grid place-items-center bg-white border border-slate-300 hover:bg-red-50 hover:text-red-600" data-testid={`customer-photo-remove-${fid}`}>
                      <X size={12} weight="bold" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn-primary" data-testid="customer-save-button" disabled={saving || uploading}>
            {saving ? "Saving…" : (editing ? "Update" : "Create")}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function Customers() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");

  const fetchCustomers = async () => {
    const { data } = await api.get("/customers");
    return data;
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await fetchCustomers();
        if (alive) setItems(data);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  const load = async () => {
    try { const data = await fetchCustomers(); setItems(data); }
    catch (e) { devWarn("Failed to reload customers:", e?.message || e); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this customer? This won't delete their quotes.")) return;
    try {
      await api.delete(`/customers/${id}`);
      toast.success("Customer deleted");
      setItems((x) => x.filter((c) => c.id !== id));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const filtered = useMemo(() => {
    if (!q) return items;
    const t = q.toLowerCase();
    return items.filter((c) =>
      [c.name, c.email, c.phone, c.address, c.city, c.postcode].some((x) => (x || "").toLowerCase().includes(t)),
    );
  }, [items, q]);

  return (
    <div className="p-6 lg:p-10 max-w-[1400px]">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-8">
        <div>
          <span className="hero-rule" />
          <div className="overline">Directory</div>
          <h1 className="font-display text-4xl lg:text-5xl font-bold tracking-tight mt-2">Customers</h1>
        </div>
        <button onClick={() => { setEditing(null); setOpen(true); }} className="btn-primary flex items-center gap-2" data-testid="customers-new-button">
          <Plus size={16} weight="bold" /> New Customer
        </button>
      </div>

      <div className="card-elev p-4 mb-4">
        <input
          data-testid="customers-search"
          className="rqp w-full max-w-md"
          placeholder="Search customers by name, address, phone…"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
      </div>

      {loading ? (
        <div className="text-sm text-slate-500" data-testid="customers-loading">Loading…</div>
      ) : filtered.length === 0 ? (
        <div className="card-elev p-12 text-center">
          <div className="font-display text-xl font-semibold">No customers yet</div>
          <p className="text-sm text-slate-500 mt-1">Add your first customer to start quoting.</p>
          <button onClick={() => setOpen(true)} className="btn-primary mt-4" data-testid="customers-empty-cta">+ Add Customer</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((c) => (
            <div key={c.id} className="card-elev p-5" data-testid={`customer-card-${c.id}`}>
              <div className="flex items-start justify-between">
                <div>
                  <div className="font-display text-lg font-bold tracking-tight">{c.name}</div>
                  <div className="text-xs text-slate-500 mt-0.5 font-mono-data">{c.postcode || "—"}</div>
                  <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                    <span
                      className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-sm border ${LEAD_STATUS_BADGE[c.lead_status || "new"] || LEAD_STATUS_BADGE.new}`}
                      data-testid={`customer-status-badge-${c.id}`}
                    >
                      {c.lead_status || "new"}
                    </span>
                    {c.source && (
                      <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-sm border bg-white text-slate-600 border-slate-300">
                        {c.source}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex gap-1">
                  <button className="btn-secondary !px-2 !py-1" onClick={() => { setEditing(c); setOpen(true); }} data-testid={`customer-edit-${c.id}`}><Pencil size={14} weight="bold" /></button>
                  <button className="btn-secondary !px-2 !py-1 text-red-600 hover:bg-red-50" onClick={() => remove(c.id)} data-testid={`customer-delete-${c.id}`}><Trash size={14} weight="bold" /></button>
                </div>
              </div>
              <div className="mt-3 space-y-1 text-sm text-slate-600">
                {c.email && <div className="flex items-center gap-2"><EnvelopeSimple size={14} className="text-slate-400" /> <span className="font-mono-data text-xs">{c.email}</span></div>}
                {c.phone && <div className="flex items-center gap-2"><Phone size={14} className="text-slate-400" /> <span className="font-mono-data text-xs">{c.phone}</span></div>}
                {(c.address || c.city) && (
                  <div className="flex items-start gap-2"><MapPin size={14} className="text-slate-400 mt-0.5" /> <span className="text-xs">{[c.address, c.city].filter(Boolean).join(", ")}</span></div>
                )}
              </div>
              {(c.photos || []).length > 0 && (
                <div className="mt-3 flex gap-1.5 overflow-x-auto" data-testid={`customer-photos-${c.id}`}>
                  {c.photos.slice(0, 5).map((fid) => (
                    <AuthImg key={fid} fileId={fid} className="w-14 h-14 object-cover border border-slate-200 shrink-0" />
                  ))}
                  {c.photos.length > 5 && (
                    <div className="w-14 h-14 grid place-items-center border border-slate-200 bg-slate-50 text-xs font-mono-data text-slate-600 shrink-0">+{c.photos.length - 5}</div>
                  )}
                </div>
              )}
              <Link to={`/quotes/new?customer=${c.id}`} className="block mt-4 btn-secondary text-center text-xs" data-testid={`customer-quote-${c.id}`}>
                New quote for {c.name.split(" ")[0]}
              </Link>
            </div>
          ))}
        </div>
      )}

      <CustomerDialog open={open} onClose={() => setOpen(false)} editing={editing} onSaved={(saved) => {
        setItems((prev) => {
          const idx = prev.findIndex((p) => p.id === saved.id);
          if (idx >= 0) { const next = [...prev]; next[idx] = saved; return next; }
          return [saved, ...prev];
        });
      }} />
    </div>
  );
}
